import React, {useState} from 'react';
import {TransformWrapper} from 'react-zoom-pan-pinch';
import {Resizable} from 're-resizable';
import type {SupplementModelV1} from '@albert-io/models';
import {Text, WindowSizeConsumer} from '@albert-io/atomic';
import {NestedMarkdown} from 'generic/MarkdownRendererV2/NestedMarkdown.react';
import useClientRect from 'lib/hooks/useClientRect';
import {SUPPLEMENT_CARD_PROPS} from 'client/components/PracticeView/Annotations/supplementCard';
import {
  NoTranslationWarningHeader,
  notAvailableText
} from 'client/components/QuestionTypes/shared/TranslationSection';

import {HiddenImage} from './HiddenImage.react';
import {ImageControls} from './ImageControls.react';
import {ImageFullScreenModal} from './ImageFullScreenModal.react';
import {ImageWrapper} from './ImageWrapper.react';

import {MIN_ZOOM, MAX_ZOOM, DEFAULT_HEIGHT, MAX_HEIGHT, MIN_HEIGHT} from './constants';

import './image-v2.scss';

interface ImageV2Props {
  content?: any;
  supplement: SupplementModelV1;
  showHideImage?: boolean;
  showFullScreen?: boolean;
  showZoom?: boolean;
  noTranslation?: boolean;
  showTranslationInfo?: boolean;
  showTranslatedSupplements?: boolean;
}

export const ImageV2 = ({
  supplement,
  content,
  showHideImage = true,
  showFullScreen = true,
  showZoom = true,
  noTranslation = false,
  showTranslationInfo = false,
  showTranslatedSupplements = false
}: ImageV2Props) => {
  const [captionRect, captionRef] = useClientRect();

  const [isHidden, setIsHidden] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  // used to disable pan/zoom while resizing
  const [isResizing, setIsResizing] = useState(false);
  // disable panning on mobile if we are not zoomed in
  const [shouldDisablePanning, setShouldDisablePanning] = useState(false);

  // Content is also passed down to Image V2 in the supplement renderer
  // Translated supplements pass translated caption to content
  // use old method of getting caption as fallback in case there are instances
  // where content isn't passed down

  const caption = content?.toJS?.()?.caption || supplement.getCaption();

  const showWarning = (noTranslation || !showTranslatedSupplements) && showTranslationInfo;

  const Wrapper = showWarning ? NoTranslationWarningHeader : React.Fragment;

  return (
    <div className='overflow-x-hidden'>
      <WindowSizeConsumer>
        {({breakpoints, viewportWidth}) => {
          const isMobile = viewportWidth < breakpoints.s;

          if (isHidden) {
            return <HiddenImage setIsHidden={setIsHidden} isMobile={isMobile} />;
          }

          return (
            <>
              <Wrapper text={notAvailableText} supplement>
                <Resizable
                  onResizeStart={() => setIsResizing(true)}
                  onResizeStop={() => {
                    setIsResizing(false);
                  }}
                  defaultSize={{
                    width: '100%',
                    height: DEFAULT_HEIGHT
                  }}
                  minWidth='100%'
                  minHeight={MIN_HEIGHT + (captionRect?.height || 0)} // account for height of dynamic caption so that it always fits
                  maxHeight={MAX_HEIGHT}
                  className='image-supplement__resizable'
                  bounds='parent'
                  enable={isMobile ? false : undefined}
                >
                  <figure
                    className='image-supplement__figure'
                    data-testid='image-supplement-default-display'
                    {...SUPPLEMENT_CARD_PROPS}
                  >
                    <div className='image-supplement__image-container'>
                      <TransformWrapper
                        centerOnInit
                        limitToBounds
                        minScale={MIN_ZOOM}
                        maxScale={MAX_ZOOM}
                        disabled={isResizing}
                        pinch={{
                          step: 10
                        }}
                        wheel={{
                          wheelDisabled: true
                        }}
                        panning={{
                          disabled: shouldDisablePanning
                        }}
                      >
                        {/**
                         * controls are visible via hover on desktop and always visible on mobile
                         * */}
                        <ImageControls
                          isMobile={isMobile}
                          showHideImage={showHideImage}
                          setIsHidden={setIsHidden}
                          showFullScreen={showFullScreen}
                          setIsFullScreen={setIsFullScreen}
                          showZoom={showZoom}
                          setShouldDisablePanning={setShouldDisablePanning}
                        />
                        <ImageWrapper
                          className='image-supplement__image'
                          supplement={supplement}
                          shouldCenter={!isResizing}
                        />
                      </TransformWrapper>
                    </div>
                    {caption && (
                      <div
                        ref={captionRef}
                        className='border-border-primary w-full border-0 border-t border-solid'
                      >
                        <Text className='p-x1 flex justify-center' as='figcaption' size='xs'>
                          <NestedMarkdown text={caption} />
                        </Text>
                      </div>
                    )}
                    {!isMobile && (
                      <div className='image-supplement__resize-handle'>
                        <div /> <div /> <div />
                      </div>
                    )}
                  </figure>
                </Resizable>
              </Wrapper>
              {isFullScreen && (
                <ImageFullScreenModal
                  isMobile={isMobile}
                  caption={caption}
                  setIsFullScreen={setIsFullScreen}
                  supplement={supplement}
                />
              )}
            </>
          );
        }}
      </WindowSizeConsumer>
    </div>
  );
};
